import React, { useEffect, useState } from 'react';
import { bookCastApi } from '../../api/bookCast';
import { storyBibleApi } from '../../api/storyBible';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { persistAcceptedDocsNote } from '../../utils/docsCharacterOffers';
import {
  CAST_LABEL,
  CAST_LEDE,
  CAST_FIND,
  CAST_NOT_NOW,
  CAST_FINDING,
  CAST_REVIEW_LEDE,
  CAST_ADD,
  CAST_ADDED,
  CAST_NONE_FOUND,
  CAST_PEOPLE,
  CAST_PLACE,
} from '../../utils/writerCopy';
import './CastOffer.css';
import '../Chat/ChatIdeaOffers.css';

const dismissKey = (storyId) => `brainstormy.cast-offer.not-now.${storyId}`;
const addedKey = (storyId) => `brainstormy.cast-offer.added.${storyId}`;

export function castDismissed(storyId) {
  try {
    return window.sessionStorage.getItem(dismissKey(storyId)) === '1';
  } catch (err) {
    return false;
  }
}

function readAddedCount(storyId) {
  try {
    const raw = window.sessionStorage.getItem(addedKey(storyId));
    if (raw == null || raw === '') return null;
    const count = Number(raw);
    return Number.isFinite(count) ? count : null;
  } catch (err) {
    return null;
  }
}

function rememberAdded(storyId, count) {
  try {
    window.sessionStorage.setItem(addedKey(storyId), String(count));
  } catch (err) {
    // Private mode: the done line lasts until the next status check.
  }
}

function dismissCast(storyId) {
  try {
    window.sessionStorage.setItem(dismissKey(storyId), '1');
  } catch (err) {
    // Private mode: the offer may come back this visit. Nothing is written.
  }
}

/**
 * #1341 — Your cast (design pass #1339, option 2A).
 * Offered when this book has talk but no people in Book Notes. Finding is one
 * paid pass that writes nothing; adding goes through Book Notes add-cards.
 */
export default function CastOffer({
  storyId,
  projectId,
  api = bookCastApi,
  persist = persistAcceptedDocsNote,
  onAdded,
  force = false,
  checkKey = null,
  showNotNow = true,
  folded = false,
}) {
  const { ask, dialog } = usePaidConfirm();
  const [foldedOpen, setFoldedOpen] = useState(false);
  const [phase, setPhase] = useState('checking');
  const [cast, setCast] = useState([]);
  const [chosen, setChosen] = useState({});
  const [error, setError] = useState('');
  const [added, setAdded] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!storyId || (!force && castDismissed(storyId))) {
      setPhase('hidden');
      return undefined;
    }
    const addedCount = readAddedCount(storyId);
    if (addedCount != null) {
      setAdded(addedCount);
      setPhase('done');
      return undefined;
    }
    setPhase((current) => (
      ['review', 'adding', 'done', 'finding', 'offer'].includes(current) ? current : 'checking'
    ));
    api.status(storyId)
      .then((status) => {
        if (cancelled) return;
        setPhase((current) => (['review', 'adding', 'done', 'finding'].includes(current)
          ? current
          : ((force && status?.enough_talk !== false) || status?.offer ? 'offer' : 'hidden')));
      })
      .catch(() => {
        if (!cancelled) setPhase('hidden');
      });
    return () => {
      cancelled = true;
    };
  }, [storyId, force, api, checkKey]);

  if (phase === 'hidden' || phase === 'checking') return null;

  const notNow = () => {
    dismissCast(storyId);
    setPhase('hidden');
  };

  const find = async () => {
    const ok = await ask('book_cast');
    if (!ok) return;
    setPhase('finding');
    setError('');
    try {
      const result = await api.propose(storyId);
      const found = result?.cast || [];
      setCast(found);
      setChosen(Object.fromEntries(found.map((item) => [item.name, true])));
      setPhase(found.length ? 'review' : 'empty');
    } catch (err) {
      setError(err?.message || 'Could not find your cast. Nothing was changed.');
      setPhase('offer');
    }
  };

  const addChosen = async () => {
    const picks = cast.filter((item) => chosen[item.name]);
    if (!picks.length) return;
    const ok = await ask('add_book_notes', { title: CAST_ADD(picks.length) });
    if (!ok) return;
    setPhase('adding');
    setError('');
    let count = 0;
    try {
      for (const item of picks) {
        // eslint-disable-next-line no-await-in-loop
        await persist({
          projectId,
          storyId,
          altitude: 'book',
          disposition: 'new',
          kind: item.kind === 'place' ? 'place' : 'people',
          title: item.name,
          statement: item.portrait,
          addCards: (id, cards) => storyBibleApi.addCards(id, cards, null, true),
        });
        count += 1;
      }
      setAdded(count);
      rememberAdded(storyId, count);
      setPhase('done');
      onAdded?.(count);
    } catch (err) {
      setAdded(count);
      setError(err?.message || 'Some notes were not added.');
      setPhase('review');
    }
  };

  const chosenCount = cast.filter((item) => chosen[item.name]).length;

  const showBody = !folded || foldedOpen;

  return (
    <section
      className={folded ? 'cast-offer cast-offer--folded idea-offers' : 'cast-offer'}
      aria-label={CAST_LABEL}
      data-testid="cast-offer"
    >
      {folded ? (
        <button
          type="button"
          className="idea-offers__count"
          aria-expanded={foldedOpen}
          onClick={() => setFoldedOpen((open) => !open)}
        >
          {CAST_LABEL}
        </button>
      ) : (
        <h3 id="cast-offer-heading" className="cast-offer__label">{CAST_LABEL}</h3>
      )}
      {showBody && phase === 'offer' && (
        <>
          <p className="cast-offer__lede">{CAST_LEDE}</p>
          <div className="cast-offer__actions">
            <button type="button" className="cd-btn cd-btn--primary" onClick={find}>{CAST_FIND}</button>
            {showNotNow ? (
              <button type="button" className="cd-btn" onClick={notNow}>{CAST_NOT_NOW}</button>
            ) : null}
          </div>
        </>
      )}
      {showBody && phase === 'finding' && <p className="cast-offer__lede" role="status">{CAST_FINDING}</p>}
      {showBody && phase === 'empty' && <p className="cast-offer__lede">{CAST_NONE_FOUND}</p>}
      {showBody && (phase === 'review' || phase === 'adding') && (
        <>
          <p className="cast-offer__lede">{CAST_REVIEW_LEDE}</p>
          <ul className="cast-offer__list">
            {cast.map((item) => (
              <li key={item.name}>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(chosen[item.name])}
                    disabled={phase === 'adding'}
                    onChange={(e) => setChosen({ ...chosen, [item.name]: e.target.checked })}
                  />
                  <span className="cast-offer__name">{item.name}</span>
                  <span className="cast-offer__kind">{item.kind === 'place' ? CAST_PLACE : CAST_PEOPLE}</span>
                </label>
                <p className="cast-offer__portrait">{item.portrait}</p>
              </li>
            ))}
          </ul>
          <div className="cast-offer__actions">
            <button
              type="button"
              className="cd-btn cd-btn--primary"
              disabled={phase === 'adding' || chosenCount === 0}
              onClick={addChosen}
            >
              {CAST_ADD(chosenCount)}
            </button>
            {showNotNow ? (
              <button type="button" className="cd-btn" disabled={phase === 'adding'} onClick={notNow}>
                {CAST_NOT_NOW}
              </button>
            ) : null}
          </div>
        </>
      )}
      {showBody && phase === 'done' && <p className="cast-offer__lede" role="status">{CAST_ADDED(added)}</p>}
      {showBody && error && <p className="cast-offer__error" role="alert">{error}</p>}
      {dialog}
    </section>
  );
}
